import type Konva from "konva";

import { type CommonShapeConfig, type Shape, ShapeType, SpotlightShapeName } from "../types/canvas";
import { InitialShapeColor } from "./colors";

const commonConfig: Konva.ShapeConfig = {
  draggable: true,
  rotation: 0,
  // Prevent nodes from moving outside the stage while dragging
  dragBoundFunc: function (pos) {
    const node = this as unknown as Konva.Node;
    const stage = node.getStage();
    if (!stage) return pos;

    const rect = node.getClientRect({ skipStroke: false, skipShadow: true });

    const currentX = node.x();
    const currentY = node.y();
    let dx = pos.x - currentX;
    let dy = pos.y - currentY;

    const proposedLeft = rect.x + dx;
    const proposedTop = rect.y + dy;
    const proposedRight = proposedLeft + rect.width;
    const proposedBottom = proposedTop + rect.height;

    const stageWidth = stage.width();
    const stageHeight = stage.height();

    // Clamp horizontally
    if (proposedLeft < 0) {
      dx = dx - proposedLeft;
    }
    if (proposedRight > stageWidth) {
      dx = dx - (proposedRight - stageWidth);
    }

    // Clamp vertically
    if (proposedTop < 0) {
      dy = dy - proposedTop;
    }
    if (proposedBottom > stageHeight) {
      dy = dy - (proposedBottom - stageHeight);
    }

    return { x: currentX + dx, y: currentY + dy };
  },
};

const ShapeConfig: Record<ShapeType, (params: CommonShapeConfig) => Konva.ShapeConfig> = {
  [ShapeType.Rectangle]: ({ type, startX, startY }) => ({
    ...InitialShapeColor[type],
    x: startX,
    y: startY,
    width: 0,
    height: 0,
    strokeWidth: 4,
    strokeScaleEnabled: true,
  }),
  [ShapeType.Ellipse]: ({ type, startX, startY }) => ({
    ...InitialShapeColor[type],
    x: startX,
    y: startY,
    radiusX: 0,
    radiusY: 0,
    strokeWidth: 4,
    strokeScaleEnabled: true,
  }),
  [ShapeType.Line]: ({ type, startX, startY }) => ({
    ...InitialShapeColor[type],
    strokeWidth: 4,
    lineCap: "round",
    lineJoin: "round",
    points: [startX, startY],
    strokeScaleEnabled: true,
    hitStrokeWidth: 30,
  }),
  [ShapeType.Arrow]: ({ type, startX, startY }) => ({
    ...InitialShapeColor[type],
    strokeWidth: 3,
    points: [startX, startY, startX, startY],
    strokeScaleEnabled: true,
    hitStrokeWidth: 30,
  }),
  [ShapeType.Text]: ({ type, startX, startY }) => ({
    ...InitialShapeColor[type],
    x: startX,
    y: startY,
    text: "",
    fontSize: 20,
    fontFamily: "Arial",
  }),
  [ShapeType.Spotlight]: ({ type, startX, startY }) => ({
    ...InitialShapeColor[type],
    x: startX,
    y: startY,
    radiusX: 0,
    radiusY: 0,
    strokeWidth: 0,
    name: SpotlightShapeName.ELLIPSE,
    strokeScaleEnabled: false,
  }),
};

export const getShapeConfig = (payload: CommonShapeConfig, overrides: Partial<Shape> = {}): Shape => {
  const shape: Shape = {
    id: payload.id,
    layerIndex: payload.layerIndex,
    type: payload.type,
    startX: payload.startX,
    startY: payload.startY,
    ...commonConfig,
    ...ShapeConfig[payload.type](payload),
    ...overrides,
  };

  return shape;
};
